import {
    DAuditInfo,
    IAuditStamps,
    RhDateInput,
    RhFormWrapper,
    RhTabsWrapper,
} from '../../../../../dum';
import {
    FIELD_LABELS,
    getDateFromString,
    getStringFromDate,
    shortLabel,
    transformToNumber,
} from '../../../../../../helpers';
import { OrderAdjustmentTypeInput } from './order-adjustment-type/order-adjustment-type-input';
import { OrderAdjustmentProductsColumns } from './order-adjustment-products/order-adjustment-products-columns';
import React, { useMemo } from 'react';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import { isProductUnique } from './validation/is-product-unique';
import {
    ActivityEntityName,
    useUpsertOrderAdjustmentMutation,
} from '../../../../../../services';
import { useHistorialTab } from '../../../../historial';
import { OrderSaleInput } from './order-sale/order-sale-input';
import { areUnitsLessThanOrderSaleProduct } from './validation/are-units-less-than-order-sale-product';
import { isProductInOrderSales } from './validation/is-product-in-order-sales';

export interface IOrderAdjustment {
    id?: number | null;
    date: string;
    order_adjustment_type_id?: number | null;
    order_sale?: {
        id?: number | null;
        compound_order_code: string;
    } | null;
    order_sale_adjustment_products?: {
        id?: number | null;
        kilos: number;
        product_id?: number | null;
        groups: number;
    }[];
    order_sale_products?: {
        id?: number | null;
        kilos: number;
        product_id?: number | null;
        groups: number;
    }[];
    order_adjustment_products: {
        id?: number | null;
        kilos: number;
        product?: {
            id: number;
            compound_description: string;
            current_group_weight?: number | null;
        } | null;
        groups: number;
        group_weight?: number | null;
    }[];
}

interface IOrderAdjustmentForm {
    // Audit stamps ride alongside the form values — display only, never
    // submitted, and kept out of IOrderAdjustment (see IAuditStamps).
    orderAdjustment?: (IOrderAdjustment & IAuditStamps) | null;
    onSuccess: () => void;
    disabled?: boolean;
    // When set (Devoluciones page), a new adjustment is preseeded with this
    // type and the type input is shown read-only. Ignored when editing an
    // existing adjustment, which keeps its own stored type.
    lockedOrderAdjustmentTypeId?: number;
}

// "Devolucion" order-adjustment type (order_adjustment_type.id). This id is the
// one type that requires an associated sale, and it backs the Devoluciones page.
export const ORDER_ADJUSTMENT_TYPE_DEVOLUCION_ID = 6;

export const ORDER_ADJUSTMENT_PRODUCTS: keyof IOrderAdjustment =
    'order_adjustment_products';

export const ORDER_SALE_PRODUCTS: keyof IOrderAdjustment =
    'order_sale_products';
export const ORDER_SALE_ADJUSTMENT_PRODUCTS: keyof IOrderAdjustment =
    'order_sale_adjustment_products';

export function OrderAdjustmentForm({
    orderAdjustment,
    onSuccess,
    disabled = false,
    lockedOrderAdjustmentTypeId,
}: IOrderAdjustmentForm) {
    const [mutation, { loading }] = useUpsertOrderAdjustmentMutation();

    const historialTab = useHistorialTab({
        entityName: ActivityEntityName.OrderAdjustment,
        entityId: orderAdjustment?.id,
        unsavedMessage:
            'El historial aparece una vez que el ajuste ha sido guardado.',
        emptyMessage: 'No hay actividades registradas para este ajuste.',
    });

    const initialValues: IOrderAdjustment = useMemo<IOrderAdjustment>(() => {
        return {
            id: orderAdjustment?.id || undefined,
            date: orderAdjustment
                ? getDateFromString(orderAdjustment.date)
                : '',
            order_adjustment_type_id:
                orderAdjustment?.order_adjustment_type_id ||
                lockedOrderAdjustmentTypeId ||
                null,
            order_sale: orderAdjustment?.order_sale || null,
            order_sale_adjustment_products:
                orderAdjustment?.order_sale_adjustment_products || [],
            order_sale_products: orderAdjustment?.order_sale_products || [],
            order_adjustment_products:
                orderAdjustment?.order_adjustment_products || [
                    {
                        product: null,
                        group_weight: 0,
                        groups: 0,
                        kilos: 0,
                    },
                ],
        };
    }, [orderAdjustment, lockedOrderAdjustmentTypeId]);

    const validationSchema: SchemaOf<IOrderAdjustment> = yup.object({
        id: yup.number().notRequired().nullable(),
        date: yup.string().required(`'Fecha' es obligatorio`).nullable(),
        order_adjustment_type_id: yup
            .number()
            .required(`'Tipo de ajuste' es obligatorio`)
            .nullable(),
        order_sale: yup.mixed().when('order_adjustment_type_id', {
            is: (val: any) => {
                return val === ORDER_ADJUSTMENT_TYPE_DEVOLUCION_ID;
            },
            then: (schema) => schema.required(`'Venta' es obligatorio`),
            otherwise: (schema) => schema.notRequired(),
        }),
        order_sale_adjustment_products: yup.mixed().notRequired(),
        order_sale_products: yup.mixed().notRequired(),
        order_adjustment_products: yup
            .array()
            .of(
                yup.object().shape({
                    id: yup.number().notRequired().nullable(),
                    product: yup
                        .mixed()
                        .required(`'Producto' es obligatorio`)
                        .nullable()
                        .test('is-product-unique', isProductUnique)
                        .test(
                            'is-product-in-order-sales',
                            isProductInOrderSales,
                        ),
                    kilos: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Cantidad' es obligatorio`)
                        .test(
                            `are-units-less-than-the-order-sale-product`,
                            areUnitsLessThanOrderSaleProduct('kilos'),
                        ),
                    groups: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Grupos' es obligatorio`)
                        .test(
                            `are-units-less-than-the-order-sale-product`,
                            areUnitsLessThanOrderSaleProduct('groups'),
                        ),
                    group_weight: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Peso' es obligatorio`),
                }),
            )
            .required(`'Productos' es obligatorio`),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading || disabled}
            actionBarStart={
                orderAdjustment?.id ? (
                    <DAuditInfo
                        created_by={orderAdjustment.created_by}
                        updated_by={orderAdjustment.updated_by}
                        createdLabel={'Creado por'}
                        updatedLabel={'Actualizado por'}
                    />
                ) : null
            }
            onSubmit={async (data) => {
                try {
                    await mutation({
                        variables: {
                            OrderAdjustmentInput: {
                                id: orderAdjustment?.id || null,
                                date: getStringFromDate(data.date),
                                order_adjustment_type_id:
                                    data?.order_adjustment_type_id || null,
                                order_sale_id: data?.order_sale?.id || null,
                                order_adjustment_products:
                                    data.order_adjustment_products.map(
                                        (productionProduct) => {
                                            return {
                                                // Round-tripped so an unchanged
                                                // row is updated in place
                                                // instead of deleted and
                                                // recreated.
                                                id:
                                                    productionProduct.id ??
                                                    null,
                                                kilos: productionProduct.kilos,
                                                product_id:
                                                    productionProduct.product!
                                                        .id,
                                                group_weight:
                                                    productionProduct.group_weight!,
                                                groups: productionProduct.groups,
                                            };
                                        },
                                    ),
                            },
                        },
                    });
                    onSuccess();
                } catch (e) {
                    console.error(e);
                }
            }}
            onInvalid={async (errors) => {
                console.error(errors);
            }}
        >
            <RhTabsWrapper
                tabs={[
                    {
                        label: 'General',
                        fields: ['date', 'order_adjustment_type_id', 'order_sale'],
                        content: (
                            <>
                                <RhDateInput
                                    name={'date'}
                                    label={shortLabel(FIELD_LABELS.date)}
                                />
                                <OrderAdjustmentTypeInput
                                    disabled={
                                        lockedOrderAdjustmentTypeId != null
                                    }
                                />
                                <OrderSaleInput />
                            </>
                        ),
                    },
                    {
                        label: 'Productos',
                        fields: [ORDER_ADJUSTMENT_PRODUCTS],
                        content: (
                            <OrderAdjustmentProductsColumns
                                initialValues={initialValues}
                            />
                        ),
                    },
                    // Empty for non-admins, so they get no tab at all.
                    ...historialTab,
                ]}
            />
        </RhFormWrapper>
    );
}
